import { combineReducers } from 'redux';
import { put, cancelled, select, call } from 'redux-saga/effects';
import { createAsyncAction, createAction, createReducer } from 'typesafe-actions';
import { api } from 'api';
import { getAllCookies, getEnv } from 'common/selectors';
import log from 'loglevel';
import { EffectR } from 'types/utils';
import {
  isCalculatorAvailable,
  isLaminateCalculator,
  LAMINATE_CONFIGURATION_WIDGET_AB_KEY,
  LAMINATE_PROJECT_CONFIGURATION_ID_COOKIE_NAME,
} from 'features/product-info';
import { ProjectConfigurationParameters } from '../types';
import { fetchProjectConfiguration } from './api';

interface ApiErrorResponse {
  status: number;
  statusText?: string;
}

function isErrorResponse(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'status' in response &&
    typeof (response as ApiErrorResponse).status === 'number' &&
    (response as ApiErrorResponse).status >= 400
  );
}

export const FETCH_LAMINATE_CONFIGURATION = createAsyncAction(
  'configurations/FETCH_LAMINATE_CONFIGURATION/request',
  'configurations/FETCH_LAMINATE_CONFIGURATION/success',
  'configurations/FETCH_LAMINATE_CONFIGURATION/failure',
  'configurations/FETCH_LAMINATE_CONFIGURATION/cancel',
)<void, ProjectConfigurationParameters, Response | Error, void>();

export const RESET_LAMINATE_CONFIGURATION = createAction(
  'configurations/RESET_LAMINATE_CONFIGURATION',
)<void>();
export const SET_LAMINATE_CONFIGURATION = createAction(
  'configurations/SET_LAMINATE_CONFIGURATION',
)<ProjectConfigurationParameters>();

export const laminateConfigurationActions = {
  FETCH_LAMINATE_CONFIGURATION,
  RESET_LAMINATE_CONFIGURATION,
  SET_LAMINATE_CONFIGURATION,
};

const error = createReducer<Response | Error | null>(null)
  .handleAction([FETCH_LAMINATE_CONFIGURATION.failure], (_, { payload }) => payload)
  .handleAction(
    [
      FETCH_LAMINATE_CONFIGURATION.request,
      FETCH_LAMINATE_CONFIGURATION.success,
      RESET_LAMINATE_CONFIGURATION,
    ],
    () => null,
  );

const loading = createReducer(false)
  .handleAction([FETCH_LAMINATE_CONFIGURATION.request], () => true)
  .handleAction(
    [
      FETCH_LAMINATE_CONFIGURATION.success,
      FETCH_LAMINATE_CONFIGURATION.failure,
      FETCH_LAMINATE_CONFIGURATION.cancel,
      RESET_LAMINATE_CONFIGURATION,
    ],
    () => false,
  );

const configuration = createReducer<ProjectConfigurationParameters | null>(null)
  .handleAction(
    [
      FETCH_LAMINATE_CONFIGURATION.failure,
      FETCH_LAMINATE_CONFIGURATION.cancel,
      RESET_LAMINATE_CONFIGURATION,
    ],
    () => null,
  )
  .handleAction(
    [FETCH_LAMINATE_CONFIGURATION.success, SET_LAMINATE_CONFIGURATION],
    (_, { payload }) => payload,
  );

export const laminate = combineReducers({
  configuration,
  loading,
  error,
});

export function* getLaminateConfiguration() {
  const isCalcAvailable: boolean = yield select(isCalculatorAvailable);
  const isLaminateCalc: boolean = yield select(isLaminateCalculator);
  const cookies: { [x: string]: string } = yield select(getAllCookies);
  const projectConfigurationId = cookies[LAMINATE_PROJECT_CONFIGURATION_ID_COOKIE_NAME];
  const isLaminateConfigurationABEnabled = cookies[LAMINATE_CONFIGURATION_WIDGET_AB_KEY] === 'true';

  if (isCalcAvailable && isLaminateCalc && projectConfigurationId && isLaminateConfigurationABEnabled) {
    yield call(doFetchLaminateConfiguration);
  }
}

export function* doFetchLaminateConfiguration() {
  const cookies: { [x: string]: string } = yield select(getAllCookies);
  const projectConfigurationId = cookies[LAMINATE_PROJECT_CONFIGURATION_ID_COOKIE_NAME];
  const abortController = (yield call([api, 'abortController'])) as EffectR.Call<
    (typeof api)['abortController']
  >;

  yield put(FETCH_LAMINATE_CONFIGURATION.request());
  const { requestID } = (yield select(getEnv)) as EffectR.Select<typeof getEnv>;
  yield call(log.info, 'Laminate project configuration was requested', requestID);

  try {
    const data = (yield call(
      fetchProjectConfiguration,
      projectConfigurationId,
      abortController.token,
    )) as EffectR.Call<typeof fetchProjectConfiguration>;
    if (isErrorResponse(data)) {
      throw new Error(`API Error ${data.status}: ${data.statusText || 'Unknown error'}`);
    }
    yield put(FETCH_LAMINATE_CONFIGURATION.success(data.projectConfigurationParameters || []));
    yield call(log.info, 'Laminate project configuration was loaded', requestID);
  } catch (e) {
    if (e instanceof Error) {
      yield put(FETCH_LAMINATE_CONFIGURATION.failure(e));
      yield call(log.info, 'Laminate project configuration has error', requestID, e);
    }
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
      yield call(log.info, 'Laminate project configuration was cancelled', requestID);
      yield put(FETCH_LAMINATE_CONFIGURATION.cancel());
    }
  }
}
